import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  TextField,
  InputAdornment,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';

interface Tenant {
  id: string;
  name: string;
  subdomain: string;
  plan: string;
  status: string;
  user_count: number;
  storage_used_mb: number;
  created_at: string;
}

const columns: GridColDef[] = [
  { field: 'name', headerName: 'テナント名', width: 200 },
  { field: 'subdomain', headerName: 'サブドメイン', width: 150 },
  {
    field: 'plan',
    headerName: 'プラン',
    width: 120,
    renderCell: (params) => (
      <Chip
        label={params.value}
        size="small"
        color={
          params.value === 'enterprise'
            ? 'primary'
            : params.value === 'professional'
            ? 'secondary'
            : 'default'
        }
      />
    ),
  },
  {
    field: 'status',
    headerName: 'ステータス',
    width: 120,
    renderCell: (params) => (
      <Chip
        label={params.value === 'active' ? 'アクティブ' : '停止中'}
        size="small"
        color={params.value === 'active' ? 'success' : 'error'}
      />
    ),
  },
  { field: 'user_count', headerName: 'ユーザー数', width: 100, type: 'number' },
  {
    field: 'storage_used_mb',
    headerName: '使用容量 (MB)',
    width: 130,
    type: 'number',
    valueFormatter: (params) => (params.value ?? 0).toFixed(1),
  },
  {
    field: 'created_at',
    headerName: '作成日',
    width: 150,
    valueFormatter: (params) =>
      new Date(params.value).toLocaleDateString('ja-JP'),
  },
];

function Tenants() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      setLoading(true);
      const data = await apiService.getAllTenants();
      
      // データが配列であることを確認し、必要なフィールドを追加
      if (Array.isArray(data)) {
        const processedData = data.map(tenant => ({
          ...tenant,
          user_count: tenant.user_count ?? 0,
          storage_used_mb: tenant.storage_used_mb ?? 0,
          plan: tenant.plan ?? 'basic',
          status: tenant.is_active ? 'active' : 'inactive'
        }));
        setTenants(processedData);
      } else {
        console.error('Tenants data is not an array:', data);
        setTenants([]);
      }
    } catch (error) {
      console.error('Failed to load tenants:', error);
      console.error('Error details:', error);
      // エラーの場合は空配列
      setTenants([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (params: any) => {
    navigate(`/tenants/${params.row.id}`);
  };

  const filteredTenants = Array.isArray(tenants) ? tenants.filter(
    (tenant) =>
      tenant.name?.toLowerCase().includes(searchText.toLowerCase()) ||
      tenant.subdomain?.toLowerCase().includes(searchText.toLowerCase())
  ) : [];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">テナント管理</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/tenants/new')}
        >
          新規テナント
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="テナント名またはサブドメインで検索"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={filteredTenants}
          columns={columns}
          loading={loading}
          onRowClick={handleRowClick}
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
          }}
        />
      </Paper>
    </Box>
  );
}

export default Tenants;